import UserEventTypes from './UserEventTypes';

const INVENTORY_WIDTH = 5;
const INVENTORY_HEIGHT = 5;
const MAXIMUM_STACK_SIZE = 20;

export function normalizeFieldNames(obj) {
  if (Array.isArray(obj)) {
    return obj.map(normalizeFieldNames);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const normalizedKey = key.charAt(0).toLowerCase() + key.slice(1);
      acc[normalizedKey] = normalizeFieldNames(obj[key]);
      return acc;
    }, {});
  }
  return obj;
}

function normalizedStats(value) {
  if (typeof value === 'string') {
    try {
      return normalizeFieldNames(JSON.parse(value));
    } catch {
      return null;
    }
  }
  return value && typeof value === 'object'
    ? normalizeFieldNames(value)
    : null;
}

function parsedObject(value) {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value && typeof value === 'object' ? value : null;
}

function normalizedArray(value) {
  const parsed = parsedObject(value);
  return Array.isArray(parsed) ? normalizeFieldNames(parsed) : null;
}

function normalizedMovementPath(value) {
  return (normalizedArray(value) || []).flatMap(step => {
    const x = Number(step?.x);
    const y = Number(step?.y);
    return Number.isInteger(x) && Number.isInteger(y) ? [{ x, y }] : [];
  });
}

function withPlayerRuntime(user, playerRuntime, currentHp) {
  if (!user?.userCombat) return user;
  const parsedRuntime = parsedObject(playerRuntime);
  const normalizedRuntime = parsedRuntime
    ? normalizeFieldNames(parsedRuntime)
    : null;
  const existingRuntime = user.userCombat.playerRuntime;
  if (!normalizedRuntime && currentHp === undefined) return user;
  return {
    ...user,
    userCombat: {
      ...user.userCombat,
      playerRuntime: normalizedRuntime
        ?? (existingRuntime
          ? { ...existingRuntime, hpCurrent: Number(currentHp) }
          : existingRuntime),
    },
  };
}

function collectionRewardInventory(user, details) {
  const materialized = parsedObject(details.inventory);
  if (Array.isArray(materialized)) {
    return normalizeFieldNames(materialized);
  }

  const itemId = details.rewardItemId;
  const quantity = Number(details.rewardQuantity);
  const inventory = Array.isArray(user.inventory) ? user.inventory : [];
  if (typeof itemId !== 'string' || !itemId
    || !Number.isInteger(quantity)
    || quantity <= 0
    || quantity > MAXIMUM_STACK_SIZE) {
    return inventory;
  }

  const stackIndex = inventory.findIndex(item => (
    item.itemId === itemId
    && !item.itemInstanceId
    && Number.isFinite(Number(item.quantity))
    && Number(item.quantity) + quantity <= MAXIMUM_STACK_SIZE
  ));
  if (stackIndex >= 0) {
    return inventory.map((item, index) => (
      index === stackIndex
        ? { ...item, quantity: Number(item.quantity) + quantity }
        : item
    ));
  }

  for (let x = 0; x < INVENTORY_WIDTH; x += 1) {
    for (let y = 0; y < INVENTORY_HEIGHT; y += 1) {
      const occupied = inventory.some(item => (
        Number(item.x) === x && Number(item.y) === y
      ));
      if (!occupied) {
        return [...inventory, {
          itemId,
          itemInstanceId: '',
          quantity,
          x,
          y,
        }];
      }
    }
  }
  return inventory;
}

function normalizeEvent(evt) {
  if (!evt || typeof evt !== 'object') return evt;
  return {
    ...evt,
    EventType: evt.EventType ?? evt.eventType,
    Details: normalizeFieldNames(evt.Details ?? evt.details ?? {}),
    Id: evt.Id ?? evt.id,
    CreatedAt: evt.CreatedAt ?? evt.createdAt,
    User: evt.User ?? evt.user ?? null,
  };
}

export function reduceInventoryEvent(user, evt) {
  const d = evt?.Details || evt?.details || {};
  switch (evt?.EventType || evt?.eventType) {
    case 'InventoryAddItem': {
      const itemId = d.itemId;
      const itemInstanceId = d.itemInstanceId;
      const quantity = Number(d.quantity);
      const x = Number(d.x);
      const y = Number(d.y);
      const inventory = Array.isArray(user.inventory) ? user.inventory : [];
      const idx = inventory.findIndex(it => (
        it.x === x
        && it.y === y
        && (
          (itemInstanceId && it.itemInstanceId === itemInstanceId)
          || (!itemInstanceId && it.itemId === itemId)
        )
      ));
      if (idx === -1) {
        return {
          ...user,
          inventory: [...inventory, {
            itemId,
            itemInstanceId,
            quantity,
            x,
            y,
          }],
        };
      }
      const nextInventory = inventory.map((it, index) => (
        index === idx
          ? { ...it, quantity: Number(it.quantity) + quantity }
          : it
      ));
      return { ...user, inventory: nextInventory };
    }
    case 'InventoryRemoveItem': {
      const itemId = d.itemId;
      const itemInstanceId = d.itemInstanceId;
      const quantity = Number(d.quantity);
      const x = Number(d.x ?? d.sourceX);
      const y = Number(d.y ?? d.sourceY);
      const inventory = Array.isArray(user.inventory) ? user.inventory : [];
      const hasSourceCoordinates = Number.isFinite(x) && Number.isFinite(y);
      if (!itemInstanceId && !itemId) {
        return user;
      }
      const matchesItem = item => (
        (itemInstanceId && item.itemInstanceId === itemInstanceId)
        || (!itemInstanceId && (!itemId || item.itemId === itemId))
      );
      if (!Number.isFinite(quantity) || quantity <= 0) {
        return user;
      }
      if (!hasSourceCoordinates) {
        let remaining = quantity;
        let changed = false;
        const nextInventory = [];
        inventory.forEach(item => {
          if (remaining <= 0 || !matchesItem(item)) {
            nextInventory.push(item);
            return;
          }
          const currentQuantity = Number(item.quantity);
          if (!Number.isFinite(currentQuantity) || currentQuantity <= 0) {
            nextInventory.push(item);
            return;
          }
          changed = true;
          if (remaining < currentQuantity) {
            nextInventory.push({
              ...item,
              quantity: currentQuantity - remaining,
            });
            remaining = 0;
          } else {
            remaining -= currentQuantity;
          }
        });
        return changed ? { ...user, inventory: nextInventory } : user;
      }
      const idx = inventory.findIndex(it => (
        Number(it.x) === x
        && Number(it.y) === y
        && matchesItem(it)
      ));
      if (idx === -1) {
        return user;
      }
      const currentQuantity = Number(inventory[idx].quantity);
      if (!Number.isFinite(currentQuantity) || quantity >= currentQuantity) {
        return {
          ...user,
          inventory: inventory.filter((_, index) => index !== idx),
        };
      }
      return {
        ...user,
        inventory: inventory.map((item, index) => (
          index === idx
            ? { ...item, quantity: currentQuantity - quantity }
            : item
        )),
      };
    }
    default:
      return user;
  }
}

function clearIdlePause(user) {
  const next = {
    ...user,
    idleSimulationPausedAt: 0,
  };
  delete next.idleSimulatedUntil;
  delete next.idlePauseAllowedMs;
  return next;
}

function applyDesiredAction(user, desiredAction) {
  if (typeof desiredAction !== 'string' || !desiredAction) {
    return user;
  }

  const next = clearIdlePause(user);
  next.desiredAction = desiredAction;
  if (desiredAction !== 'NONE') {
    next.idleResumeAction = desiredAction;
  } else {
    delete next.idleResumeAction;
  }
  return next;
}

function reduceEvent(user, evt) {
  const d = evt.Details || {};
  switch (evt.EventType) {
    case 'SetLocation': {
      const next = { ...user, lastLocationIndex: d.location };
      if (d.x !== undefined) next.lastLocationX = d.x;
      if (d.y !== undefined) next.lastLocationY = d.y;
      if (d.dx !== undefined) next.lastMoveDeltaX = d.dx;
      if (d.dy !== undefined) next.lastMoveDeltaY = d.dy;
      const movementPath = Array.isArray(user.movementPath)
        ? user.movementPath
        : [];
      if (
        Number(movementPath[0]?.x) === Number(next.lastLocationX)
        && Number(movementPath[0]?.y) === Number(next.lastLocationY)
      ) {
        next.movementPath = movementPath.slice(1);
      }
      const hasMovementStopDistance = (
        user.movementStopDistance !== null
        && user.movementStopDistance !== undefined
        && Number.isFinite(Number(user.movementStopDistance))
      );
      if (Number(next.lastLocationX) === Number(user.movementTargetX)
        && Number(next.lastLocationY) === Number(user.movementTargetY)
        && !hasMovementStopDistance) {
        next.movementTargetX = null;
        next.movementTargetY = null;
        next.movementStopDistance = null;
        next.movementArrivalAction = null;
        next.movementPath = [];
      }
      return next;
    }
    case 'SetMovementPath': {
      if (String(d.clear).toLowerCase() === 'true') {
        const cleared = clearIdlePause({
          ...user,
          movementTargetX: null,
          movementTargetY: null,
          movementStopDistance: null,
          movementArrivalAction: null,
          movementPath: [],
        });
        return applyDesiredAction(cleared, d.desiredAction);
      }
      const routed = clearIdlePause({
        ...user,
        movementTargetX: Number(d.targetX),
        movementTargetY: Number(d.targetY),
        movementStopDistance: d.stopWithinDistance === undefined
          ? null
          : Number(d.stopWithinDistance),
        movementArrivalAction: typeof d.arrivalAction === 'string'
          ? d.arrivalAction.toUpperCase()
          : null,
        movementPath: normalizedMovementPath(d.path),
      });
      return applyDesiredAction(routed, d.desiredAction);
    }
    case 'ResolveMovementDestination': {
      const resolved = {
        ...user,
        currentAction: d.currentAction || 'RESTING',
        movementTargetX: null,
        movementTargetY: null,
        movementStopDistance: null,
        movementArrivalAction: null,
        movementPath: [],
        userCombat: null,
      };
      return applyDesiredAction(resolved, 'NONE');
    }
    case 'SetRegion': {
      const regionId = d.regionId;
      return regionId ? { ...user, currentRegionId: regionId } : user;
    }
    case UserEventTypes.REGION_TRANSITIONED: {
      if (d.receiptOnly === true) return user;
      const regionId = d.regionId ?? d.targetRegionId;
      if (!regionId) return user;
      const worldInstanceId = d.worldInstanceId
        ?? d.targetWorldInstanceId
        ?? 'shared';
      const x = d.x ?? d.destinationX ?? d.targetX ?? d.entryX;
      const y = d.y ?? d.destinationY ?? d.targetY ?? d.entryY;
      const location = d.locationIndex ?? d.location;
      return {
        ...user,
        currentRegionId: regionId,
        currentWorldInstanceId: worldInstanceId,
        lastLocationIndex: location === undefined
          ? user.lastLocationIndex
          : Number(location),
        lastLocationX: x === undefined
          ? user.lastLocationX
          : Number(x),
        lastLocationY: y === undefined
          ? user.lastLocationY
          : Number(y),
        activeResourceCollection: null,
        movementTargetX: null,
        movementTargetY: null,
        movementStopDistance: null,
        movementArrivalAction: null,
        movementPath: [],
        currentAction: 'RESTING',
        desiredAction: 'NONE',
        previousAction: null,
        userCombat: null,
        lastMoveDeltaX: 0,
        lastMoveDeltaY: 0,
      };
    }
    case UserEventTypes.WORLD_INSTANCE_CHANGED: {
      if (d.receiptOnly === true) return user;
      const worldInstanceId = d.worldInstanceId;
      if (!worldInstanceId) return user;
      return {
        ...user,
        currentRegionId: d.regionId ?? user.currentRegionId,
        currentWorldInstanceId: worldInstanceId,
        lastLocationX: d.x === undefined
          ? user.lastLocationX
          : Number(d.x),
        lastLocationY: d.y === undefined
          ? user.lastLocationY
          : Number(d.y),
        activeResourceCollection: null,
        movementTargetX: null,
        movementTargetY: null,
        movementStopDistance: null,
        movementArrivalAction: null,
        movementPath: [],
        currentAction: 'RESTING',
        desiredAction: 'NONE',
        previousAction: null,
        userCombat: d.clearCombat === true
          ? null
          : user.userCombat,
        lastMoveDeltaX: 0,
        lastMoveDeltaY: 0,
      };
    }
    case UserEventTypes.RESOURCE_COLLECTION_STARTED: {
      const activeCollection = parsedObject(d.activeCollection);
      if (!activeCollection) return user;
      return {
        ...user,
        activeResourceCollection: normalizeFieldNames(activeCollection),
        movementTargetX: null,
        movementTargetY: null,
        movementStopDistance: null,
        movementArrivalAction: null,
        movementPath: [],
        previousAction: user.currentAction,
        currentAction: 'COLLECTING',
        desiredAction: 'NONE',
      };
    }
    case UserEventTypes.RESOURCE_COLLECTION_COMPLETED: {
      return {
        ...user,
        inventory: collectionRewardInventory(user, d),
        activeResourceCollection: null,
        previousAction: user.currentAction,
        currentAction: d.currentAction || 'RESTING',
        desiredAction: 'NONE',
      };
    }
    case UserEventTypes.BASE_CAMP_CHANGED: {
      const baseCamp = parsedObject(d.baseCamp);
      const inventory = parsedObject(d.inventory);
      return {
        ...user,
        baseCamp: baseCamp
          ? normalizeFieldNames(baseCamp)
          : user.baseCamp,
        inventory: Array.isArray(inventory)
          ? normalizeFieldNames(inventory)
          : user.inventory,
      };
    }
    case 'DealDmg': {
      const damage = Number(d.dmg);
      if (!Number.isFinite(damage)) return user;

      const attackerIsPlayer = String(d.attackerIsPlayer) === 'True';
      const defenderHp = Number(d.defenderHp);
      if (attackerIsPlayer) {
        const currentEnemyHp = Number(user?.userCombat?.enemy?.currentHp);
        const runtimeEnemyHp = Number(user?.userCombat?.enemyRuntime?.hpCurrent);
        const baseline = Number.isFinite(runtimeEnemyHp) ? runtimeEnemyHp : currentEnemyHp;
        if (!user?.userCombat) return user;
        if (!Number.isFinite(baseline) && !Number.isFinite(defenderHp)) return user;

        const nextHp = Number.isFinite(defenderHp)
          ? Math.max(0, defenderHp)
          : Math.max(0, baseline - damage);
        const nextCombat = { ...user.userCombat };

        if (nextCombat.enemy) {
          nextCombat.enemy = { ...nextCombat.enemy, currentHp: nextHp };
        }
        if (nextCombat.enemyRuntime) {
          nextCombat.enemyRuntime = {
            ...nextCombat.enemyRuntime,
            hpCurrent: nextHp,
            alive: nextHp > 0 ? nextCombat.enemyRuntime.alive : false
          };
        }

        return {
          ...user,
          userCombat: nextCombat
        };
      }

      const currentPlayerHp = Number(user?.currentHp);
      const runtimePlayerHp = Number(user?.userCombat?.playerRuntime?.hpCurrent);
      const baseline = Number.isFinite(runtimePlayerHp) ? runtimePlayerHp : currentPlayerHp;
      if (!Number.isFinite(baseline) && !Number.isFinite(defenderHp)) return user;

      const nextHp = Number.isFinite(defenderHp)
        ? Math.max(0, defenderHp)
        : Math.max(0, baseline - damage);
      const nextUser = { ...user };
      if (Number.isFinite(currentPlayerHp)) {
        nextUser.currentHp = nextHp;
      }

      if (user?.userCombat) {
        nextUser.userCombat = {
          ...user.userCombat,
          playerRuntime: user.userCombat.playerRuntime
            ? {
                ...user.userCombat.playerRuntime,
                hpCurrent: nextHp,
                alive: nextHp > 0 ? user.userCombat.playerRuntime.alive : false
              }
            : user.userCombat.playerRuntime
        };
      }

      return nextUser;
    }
    case 'HealthSet': {
      const currentHp = Number(d.health);
      return withPlayerRuntime(
        { ...user, currentHp },
        d.playerRuntime,
        currentHp,
      );
    }
    case 'RegenProgress': {
      let next = { ...user };
      if (d.lastRegenTs !== undefined) next.lastRegenTs = d.lastRegenTs;
      if (d.regenRemainderHp !== undefined) next.regenRemainderHp = d.regenRemainderHp;
      const currentHp = d.currentHp ?? d.health;
      if (currentHp !== undefined) next.currentHp = Number(currentHp);
      const queue = normalizedArray(d.consumableEffectQueue);
      if (queue) next.consumableEffectQueue = queue;
      const statsCached = normalizedStats(d.userStats ?? d.statsCached);
      if (statsCached) next.statsCached = statsCached;
      next = withPlayerRuntime(next, d.playerRuntime, currentHp);
      return next;
    }
    case UserEventTypes.HUNGER_PROGRESS: {
      let next = {
        ...user,
        satiety: Number(d.satiety),
        hungerState: d.hungerState || user.hungerState,
      };
      const hungerEffects = parsedObject(d.hungerEffects);
      if (hungerEffects) {
        next.hungerEffects = normalizeFieldNames(hungerEffects);
      }
      const currentHp = d.currentHp ?? d.health;
      if (currentHp !== undefined) next.currentHp = Number(currentHp);
      return withPlayerRuntime(next, d.playerRuntime, currentHp);
    }
    case 'XpSet': {
      return { ...user, xp: d.xp };
    }
    case 'XpChange': {
      return { ...user, xp: parseInt(user.xp) + d.xp };
    }
    case 'UpdateUserAction': {
      const newAction = d.userAction;
      let next = { ...user, currentAction: newAction };
      if (d.userCombat) {
        const parsed = normalizeFieldNames(JSON.parse(d.userCombat));
        next = { ...next, userCombat: parsed };
      } else if (newAction !== 'FIGHTING' && newAction !== 'FLEEING') {
        next.userCombat = null;
      }
      if ('lastActionBeforeCombat' in d) {
        const raw = d.lastActionBeforeCombat;
        if (raw === null || raw === undefined || raw === '') {
          next.lastActionBeforeCombat = null;
        } else {
          next.lastActionBeforeCombat = raw;
        }
      }
      return next;
    }
    case 'UserCombat': {
      return { ...user, combatInfo: d.combatInfo };
    }
    case 'InventoryAddItem':
    case 'InventoryRemoveItem':
      return reduceInventoryEvent(user, evt);
    case 'ResetInventory': {
      return { ...user, inventory: [] };
    }
    case 'ResetEquipment': {
      return { ...user, equipment: [] };
    }
    case UserEventTypes.RUN_RESET: {
      const snapshot = evt.User && typeof evt.User === 'object'
        ? normalizeFieldNames(evt.User)
        : null;
      const next = snapshot ? { ...snapshot } : {
        ...user,
        inventory: Array.isArray(d.inventory)
          ? normalizeFieldNames(d.inventory)
          : (d.clearInventory === true
            ? []
            : (Array.isArray(user.inventory) ? user.inventory : [])),
        equipment: Array.isArray(d.equipment)
          ? normalizeFieldNames(d.equipment)
          : (d.clearEquipment === true
            ? []
            : (Array.isArray(user.equipment) ? user.equipment : [])),
      };
      const runId = d.currentRunId ?? d.runId;
      if (runId !== undefined) next.currentRunId = runId;
      const currentHp = d.currentHp ?? d.health;
      if (currentHp !== undefined) next.currentHp = Number(currentHp);
      const statsCached = normalizedStats(d.statsCached ?? d.userStats);
      if (statsCached) next.statsCached = statsCached;
      const attributes = parsedObject(d.attributes);
      if (attributes) next.attributes = attributes;
      const regionId = d.currentRegionId ?? d.regionId;
      if (regionId) next.currentRegionId = regionId;
      const positionX = d.positionX ?? d.x;
      const positionY = d.positionY ?? d.y;
      if (positionX !== undefined) next.lastLocationX = Number(positionX);
      if (positionY !== undefined) next.lastLocationY = Number(positionY);
      next.xp = d.xp === undefined ? 0 : Number(d.xp);
      next.starterLoadoutReady = d.starterLoadoutReady === undefined
        ? Boolean(snapshot?.starterLoadoutReady)
        : Boolean(d.starterLoadoutReady);
      next.currentAction = d.currentAction || 'RESTING';
      next.desiredAction = d.desiredAction || 'NONE';
      next.userCombat = null;
      next.consumableEffectQueue = [];
      if (d.clearInventory === true) next.quickBelt = [];
      if (d.clearEquipment === true) next.quickBeltCapacity = 1;
      next.satiety = d.satiety === undefined
        ? 100
        : Number(d.satiety);
      next.hungerState = d.hungerState || 'well_fed';
      next.movementTargetX = null;
      next.movementTargetY = null;
      next.movementStopDistance = null;
      next.movementArrivalAction = null;
      next.movementPath = [];
      next.activeResourceCollection = null;
      return next;
    }
    case UserEventTypes.STARTER_LOADOUT_READY: {
      const next = {
        ...user,
        starterLoadoutReady: true,
      };
      const runId = d.currentRunId ?? d.runId;
      if (runId !== undefined) next.currentRunId = runId;
      const currentHp = d.currentHp ?? d.health;
      if (currentHp !== undefined) next.currentHp = Number(currentHp);
      const statsCached = normalizedStats(d.statsCached ?? d.userStats);
      if (statsCached) next.statsCached = statsCached;
      return next;
    }
    case UserEventTypes.CONSUME_ITEM: {
      const materializedInventory = normalizedArray(
        d.resultInventory ?? d.inventory,
      );
      let inventory = materializedInventory
        ? materializedInventory
        : (Array.isArray(user.inventory) ? user.inventory : []);
      if (!materializedInventory) {
        const itemId = d.itemId;
        const itemInstanceId = d.itemInstanceId;
        const x = Number(d.x);
        const y = Number(d.y);
        const quantityConsumed = Number(d.quantityConsumed ?? 1);
        const remainingQuantity = Number(d.remainingQuantity);
        const itemIndex = inventory.findIndex(item => (
          Number(item.x) === x
          && Number(item.y) === y
          && (
            (itemInstanceId && item.itemInstanceId === itemInstanceId)
            || (!itemInstanceId && item.itemId === itemId)
          )
        ));
        if (itemIndex >= 0 && quantityConsumed > 0) {
          const currentQuantity = Number(inventory[itemIndex].quantity);
          const nextQuantity = Number.isFinite(remainingQuantity)
            ? remainingQuantity
            : currentQuantity - quantityConsumed;
          inventory = nextQuantity > 0
            ? inventory.map((item, index) => (
              index === itemIndex
                ? { ...item, quantity: nextQuantity }
                : item
            ))
            : inventory.filter((_, index) => index !== itemIndex);
        }
      }
      const quickBelt = normalizedArray(d.resultQuickBelt);
      const next = {
        ...user,
        inventory,
        ...(quickBelt ? { quickBelt } : {}),
      };
      const currentHp = d.currentHp ?? d.health;
      if (currentHp !== undefined) next.currentHp = Number(currentHp);
      const queue = normalizedArray(d.consumableEffectQueue);
      if (queue) next.consumableEffectQueue = queue;
      const statsCached = normalizedStats(d.userStats ?? d.statsCached);
      if (statsCached) next.statsCached = statsCached;
      if (d.lastRegenTs !== undefined) next.lastRegenTs = d.lastRegenTs;
      if (d.regenRemainderHp !== undefined) next.regenRemainderHp = d.regenRemainderHp;
      if (d.satiety !== undefined) next.satiety = Number(d.satiety);
      if (d.hungerState) next.hungerState = d.hungerState;
      return withPlayerRuntime(next, d.playerRuntime, currentHp);
    }
    case UserEventTypes.QUICK_BELT_CHANGED: {
      const inventory = normalizedArray(d.inventory);
      const quickBelt = normalizedArray(d.quickBelt);
      return {
        ...user,
        ...(inventory ? { inventory } : {}),
        ...(quickBelt ? { quickBelt } : {}),
        quickBeltCapacity: Math.min(
          8,
          Math.max(1, Number(d.quickBeltCapacity) || 1),
        ),
      };
    }
    case 'UpdateUserStats': {
      try {
        const parsed = JSON.parse(d.userStats);
        return { ...user, statsCached: normalizeFieldNames(parsed) };
      } catch {
        return user;
      }
    }
    case 'EquipItem': {
      const resultInventory = normalizedArray(d.resultInventory);
      const resultEquipment = normalizedArray(d.resultEquipment);
      const quickBelt = normalizedArray(d.quickBelt);
      if (resultInventory && resultEquipment && quickBelt) {
        return {
          ...user,
          inventory: resultInventory,
          equipment: resultEquipment,
          quickBelt,
          quickBeltCapacity: Math.min(
            8,
            Math.max(1, Number(d.quickBeltCapacity) || 1),
          ),
        };
      }
      const itemInstanceId = d.itemInstanceId;
      const itemTypeId = d.itemTypeId;
      const x = d.x, y = d.y;
      // Remove from inventory slot if present
      let newInventory = user.inventory.filter(it => !(it.itemInstanceId === itemInstanceId && it.x === x && it.y === y));
      // Add/replace equipment entry for type
      const other = user.equipment.filter(e => e.itemTypeId !== itemTypeId);
      const newEquip = [...other, { itemInstanceId, itemId: d.itemId, itemTypeId }];
      return { ...user, inventory: newInventory, equipment: newEquip };
    }
    case 'UnequipItem': {
      const resultInventory = normalizedArray(d.resultInventory);
      const resultEquipment = normalizedArray(d.resultEquipment);
      const quickBelt = normalizedArray(d.quickBelt);
      if (resultInventory && resultEquipment && quickBelt) {
        return {
          ...user,
          inventory: resultInventory,
          equipment: resultEquipment,
          quickBelt,
          quickBeltCapacity: Math.min(
            8,
            Math.max(1, Number(d.quickBeltCapacity) || 1),
          ),
        };
      }
      const itemId = d.itemId;
      const itemTypeId = d.itemTypeId;
      const itemInstanceId = d.itemInstanceId;
      const x = d.x, y = d.y;
      const newEquipment = user.equipment.filter(e => e.itemTypeId !== itemTypeId);
      return { ...user, equipment: newEquipment, inventory: [...user.inventory, { itemId, itemInstanceId, quantity: 1, x, y }] };
    }
    case 'InventoryMoveItem': {
      const fromX = Number(d.fromX), fromY = Number(d.fromY);
      const toX = Number(d.toX), toY = Number(d.toY);
      const inventory = (user.inventory || []).map(item => ({ ...item }));
      const sourceIndex = inventory.findIndex(item => (
        Number(item.x) === fromX
        && Number(item.y) === fromY
        && (d.itemInstanceId
          ? item.itemInstanceId === d.itemInstanceId
          : item.itemId === d.itemId)
      ));
      if (sourceIndex < 0) return user;
      const source = inventory[sourceIndex];
      const destinationIndex = inventory.findIndex(item => (
        Number(item.x) === toX && Number(item.y) === toY
      ));
      if (destinationIndex === sourceIndex) return user;
      const destination = inventory[destinationIndex];
      const requested = Number(d.quantity);
      const quantity = requested > 0 ? requested : Number(source.quantity);
      const compatible = destination
        && d.stackable === true
        && !source.itemInstanceId
        && !destination.itemInstanceId
        && source.itemId === destination.itemId;
      if (compatible) {
        const moved = Math.min(
          quantity,
          MAXIMUM_STACK_SIZE - Number(destination.quantity),
        );
        if (moved <= 0) return user;
        destination.quantity = Number(destination.quantity) + moved;
        source.quantity = Number(source.quantity) - moved;
        if (source.quantity === 0) inventory.splice(sourceIndex, 1);
        return { ...user, inventory };
      }
      if (destination) {
        if (quantity !== Number(source.quantity)) return user;
        destination.x = fromX;
        destination.y = fromY;
      }
      if (quantity === Number(source.quantity)) {
        source.x = toX;
        source.y = toY;
      } else if (d.stackable === true && !source.itemInstanceId) {
        source.quantity = Number(source.quantity) - quantity;
        inventory.push({
          ...source,
          quantity,
          x: toX,
          y: toY,
        });
      }
      return { ...user, inventory };
    }
    case 'FleeLog': {
      return user;
    }
    case 'IdleProgressPaused': {
      const pausedAtRaw = d.pausedAt;
      const simulatedUntilRaw = d.simulatedUntil;
      const allowedMsRaw = d.allowedMs;
      const lastActivityRaw = d.lastActivity;

      const next = { ...user };
      const pausedAt = pausedAtRaw !== undefined ? Number(pausedAtRaw) : Number.NaN;
      if (!Number.isNaN(pausedAt)) {
        next.idleSimulationPausedAt = pausedAt;
      }

      const simulatedUntil = simulatedUntilRaw !== undefined ? Number(simulatedUntilRaw) : Number.NaN;
      if (!Number.isNaN(simulatedUntil)) {
        next.idleSimulatedUntil = simulatedUntil;
      }

      const allowedMs = allowedMsRaw !== undefined ? Number(allowedMsRaw) : Number.NaN;
      if (!Number.isNaN(allowedMs)) {
        next.idlePauseAllowedMs = allowedMs;
      }

      const lastActivity = lastActivityRaw !== undefined ? Number(lastActivityRaw) : Number.NaN;
      if (!Number.isNaN(lastActivity)) {
        next.lastActivity = lastActivity;
      }

      if (typeof d.currentAction === 'string' && d.currentAction.length > 0) {
        next.currentAction = d.currentAction;
      }
      if (typeof d.desiredAction === 'string' && d.desiredAction.length > 0) {
        next.desiredAction = d.desiredAction;
      }
      const resumeActionRaw = typeof d.resumeAction === 'string' ? d.resumeAction : null;
      if (resumeActionRaw && resumeActionRaw.length > 0 && resumeActionRaw !== 'NONE') {
        next.idleResumeAction = resumeActionRaw;
      } else if (typeof d.desiredAction === 'string' && d.desiredAction !== 'NONE') {
        next.idleResumeAction = d.desiredAction;
      } else if (typeof d.currentAction === 'string' && d.currentAction.length > 0) {
        next.idleResumeAction = d.currentAction;
      }

      return next;
    }
    case 'SetDesiredAction': {
      return applyDesiredAction(user, d.desiredAction);
    }
    default:
      return user;
  }
}

export function reduceReconciliationEvent(user, evt) {
  const normalizedEvent = normalizeEvent(evt);
  if (!normalizedEvent || !normalizedEvent.EventType) return user;
  return reduceEvent(user, normalizedEvent);
}

function cursorFromUser(user) {
  const timestamp = user?.latestAppliedEventAt ?? user?.lastEventSummary;
  const id = user?.latestAppliedEventId ?? user?.lastEventSummaryId ?? '';
  const timestampMs = Date.parse(timestamp);
  return Number.isFinite(timestampMs)
    ? { timestampMs, id: String(id || '') }
    : null;
}

function cursorFromEvent(evt) {
  const timestampMs = Date.parse(evt?.CreatedAt);
  return Number.isFinite(timestampMs)
    ? { timestampMs, id: String(evt?.Id || '') }
    : null;
}

function isAfterCursor(eventCursor, userCursor) {
  if (!eventCursor || !userCursor) return true;
  if (eventCursor.timestampMs !== userCursor.timestampMs) {
    return eventCursor.timestampMs > userCursor.timestampMs;
  }
  if (!eventCursor.id || !userCursor.id) return false;
  return eventCursor.id.localeCompare(userCursor.id) > 0;
}

export default function reduce(user, evt) {
  const normalizedEvent = normalizeEvent(evt);
  if (!normalizedEvent || !normalizedEvent.EventType) return user;
  const nextEventCursor = cursorFromEvent(normalizedEvent);
  if (!isAfterCursor(nextEventCursor, cursorFromUser(user))) {
    return user;
  }

  const next = reduceEvent(user, normalizedEvent);
  if (!nextEventCursor) return next;
  return {
    ...next,
    latestAppliedEventAt: normalizedEvent.CreatedAt,
    latestAppliedEventId: normalizedEvent.Id || '',
  };
}
